import dayjs from 'dayjs';
import {
  AlertCircle,
  Building2,
  Calendar,
  CalendarDays,
  CheckCircle2,
  DollarSign,
  Download,
  FileDown,
  FileSpreadsheet,
  Gift,
  Hash,
  Package,
  Percent,
  Settings2,
  ShoppingCart,
  Sparkles,
  Store,
  TableProperties,
  ToggleLeft,
  ToggleRight,
  Users,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useExportExcelCustom from '@/hooks/useExportExcelCustom';
import { cn } from '@/lib/utils';

import type {
  ICompanyItemInterface,
  ICompanyList,
} from '../../api/CompanyItems.interface';

type Props = {
  items: ICompanyItemInterface[];
  startDate: Date | null;
  endDate: Date | null;
  selectedCompany: ICompanyList | null;
};

// Column configuration with metadata
const COLUMN_CONFIG = [
  {
    key: 'ItemID',
    label: 'كود الصنف',
    exportLabel: 'Item ID',
    icon: Hash,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    category: 'item',
  },
  {
    key: 'ItemName',
    label: 'اسم الصنف',
    exportLabel: 'Item Name',
    icon: Package,
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-50 dark:bg-violet-950/30',
    category: 'item',
  },
  {
    key: 'UnitPrice',
    label: 'سعر الوحدة',
    exportLabel: 'Unit Price',
    icon: DollarSign,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/30',
    category: 'financial',
  },
  {
    key: 'Qty',
    label: 'الكمية',
    exportLabel: 'Quantity',
    icon: Package,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    category: 'quantity',
  },
  {
    key: 'Bonus',
    label: 'البونص',
    exportLabel: 'Bonus',
    icon: Gift,
    color: 'text-pink-600 dark:text-pink-400',
    bgColor: 'bg-pink-50 dark:bg-pink-950/30',
    category: 'quantity',
  },
  {
    key: 'Discount',
    label: 'الخصم',
    exportLabel: 'Discount',
    icon: Percent,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    category: 'financial',
  },
  {
    key: 'ClientID',
    label: 'كود العميل',
    exportLabel: 'Client ID',
    icon: Users,
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-50 dark:bg-cyan-950/30',
    category: 'client',
  },
  {
    key: 'ClientName',
    label: 'اسم العميل',
    exportLabel: 'Client Name',
    icon: Users,
    color: 'text-cyan-600 dark:text-cyan-400',
    bgColor: 'bg-cyan-50 dark:bg-cyan-950/30',
    category: 'client',
  },
  {
    key: 'InvoiceID',
    label: 'رقم الفاتورة',
    exportLabel: 'Invoice ID',
    icon: FileSpreadsheet,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-950/30',
    category: 'transaction',
  },
  {
    key: 'TrDate',
    label: 'التاريخ',
    exportLabel: 'Date',
    icon: CalendarDays,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-950/30',
    category: 'transaction',
  },
  {
    key: 'type',
    label: 'نوع العملية',
    exportLabel: 'Transaction Type',
    icon: ShoppingCart,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950/30',
    category: 'transaction',
  },
  {
    key: 'storeID',
    label: 'المخزن',
    exportLabel: 'Store',
    icon: Store,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-950/30',
    category: 'transaction',
  },
] as const;

type ColumnKey = (typeof COLUMN_CONFIG)[number]['key'];

function ExcelBtn({ items, endDate, selectedCompany, startDate }: Props) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<Set<ColumnKey>>(
    new Set(COLUMN_CONFIG.map((col) => col.key)),
  );
  const [selectAll, setSelectAll] = useState(true);

  // Group columns by category
  const groupedColumns = useMemo(() => {
    type ColumnConfigItem = typeof COLUMN_CONFIG[number];
    const groups = {
      item: {
        label: 'بيانات الصنف',
        icon: Package,
        columns: [] as ColumnConfigItem[],
      },
      financial: {
        label: 'البيانات المالية',
        icon: DollarSign,
        columns: [] as ColumnConfigItem[],
      },
      quantity: {
        label: 'الكميات',
        icon: Package,
        columns: [] as ColumnConfigItem[],
      },
      client: {
        label: 'بيانات العميل',
        icon: Users,
        columns: [] as ColumnConfigItem[],
      },
      transaction: {
        label: 'بيانات المعاملة',
        icon: FileSpreadsheet,
        columns: [] as ColumnConfigItem[],
      },
    };

    COLUMN_CONFIG.forEach((col) => {
      groups[col.category].columns.push(col);
    });

    return Object.entries(groups).filter(
      ([_, group]) => group.columns.length > 0,
    );
  }, []);

  const headers: Partial<Record<keyof ICompanyItemInterface, string>> =
    useMemo(() => {
      const selectedHeaders: Partial<
        Record<keyof ICompanyItemInterface, string>
      > = {};
      COLUMN_CONFIG.forEach((col) => {
        if (selectedColumns.has(col.key)) {
          selectedHeaders[col.key] = col.exportLabel;
        }
      });
      return selectedHeaders;
    }, [selectedColumns]);

  const handleToggleColumn = (columnKey: ColumnKey) => {
    const newSelected = new Set(selectedColumns);
    if (newSelected.has(columnKey)) {
      newSelected.delete(columnKey);
    } else {
      newSelected.add(columnKey);
    }
    setSelectedColumns(newSelected);
    setSelectAll(newSelected.size === COLUMN_CONFIG.length);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(COLUMN_CONFIG.map((col) => col.key)));
    }
    setSelectAll(!selectAll);
  };

  const handleCategoryToggle = (categoryColumns: typeof COLUMN_CONFIG[number][]) => {
    const categoryKeys = categoryColumns.map((col) => col.key);
    const allSelected = categoryKeys.every((key) => selectedColumns.has(key));

    const newSelected = new Set(selectedColumns);
    if (allSelected) {
      categoryKeys.forEach((key) => newSelected.delete(key));
    } else {
      categoryKeys.forEach((key) => newSelected.add(key));
    }
    setSelectedColumns(newSelected);
    setSelectAll(newSelected.size === COLUMN_CONFIG.length);
  };

  const transformData = (
    item: ICompanyItemInterface,
  ): Partial<ICompanyItemInterface> => {
    const transformed: Partial<ICompanyItemInterface> = {};

    if (selectedColumns.has('ItemID')) transformed.ItemID = item.ItemID;
    if (selectedColumns.has('ItemName')) transformed.ItemName = item.ItemName;
    if (selectedColumns.has('UnitPrice'))
      transformed.UnitPrice = item.UnitPrice;
    if (selectedColumns.has('InvoiceID'))
      transformed.InvoiceID = item.InvoiceID;
    if (selectedColumns.has('TrDate'))
      transformed.TrDate = dayjs(new Date(item.TrDate)).format('YYYY-MM-DD');
    if (selectedColumns.has('ClientID')) transformed.ClientID = item.ClientID;
    if (selectedColumns.has('ClientName'))
      transformed.ClientName = item.ClientName;
    if (selectedColumns.has('storeID')) transformed.storeID = item.storeID;
    if (selectedColumns.has('Qty')) transformed.Qty = item.Qty;
    if (selectedColumns.has('Bonus')) transformed.Bonus = item.Bonus;
    if (selectedColumns.has('Discount')) transformed.Discount = item.Discount;
    if (selectedColumns.has('type')) {
      transformed.type =
        item.type.trim() === 'بيع'
          ? 'Sale'
          : item.type.trim() === 'مرتجع عميل'
            ? 'Return_Client'
            : item.type.trim() === 'شراء'
              ? 'Purchase'
              : 'Return_Supplier';
    }

    return transformed;
  };

  const exportToExcel = useExportExcelCustom(items, {
    headers,
    transformData,
    filename: `تقرير اصناف الشركات ${selectedCompany?.Name} من ${dayjs(startDate).format('YYYY-MM-DD')} الى ${dayjs(endDate).format('YYYY-MM-DD')}.xlsx`,
  });

  const handleExport = () => {
    if (selectedColumns.size === 0) {
      // Show warning if no columns selected
      return;
    }
    exportToExcel();
    setIsDialogOpen(false);
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'غير محدد';
    return dayjs(date).format('DD/MM/YYYY');
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild>
              <Button
                size="sm"
                className={cn(
                  'relative overflow-hidden transition-all duration-300',
                  'bg-gradient-to-r from-emerald-600 via-emerald-500 to-green-500',
                  'hover:from-emerald-700 hover:via-emerald-600 hover:to-green-600',
                  'dark:from-emerald-700 dark:via-emerald-600 dark:to-green-600',
                  'dark:hover:from-emerald-800 dark:hover:via-emerald-700 dark:hover:to-green-700',
                  'text-white shadow-lg hover:scale-105 hover:shadow-xl',
                  'border border-white/20',
                  'group',
                )}
              >
                <span className="relative z-10 flex items-center gap-2">
                  <div className="relative">
                    <FileSpreadsheet className="h-4 w-4 transition-transform group-hover:rotate-12" />
                    <Sparkles className="absolute -right-1 -top-1 h-2.5 w-2.5 animate-pulse text-yellow-300" />
                  </div>
                  <span className="font-medium">تصدير Excel</span>
                  <Badge
                    variant="outline"
                    className="ml-1 h-5 border-white/30 bg-white/20 px-1.5 text-[10px] font-bold text-white"
                  >
                    {items.length}
                  </Badge>
                  <Settings2 className="h-3.5 w-3.5 opacity-70 transition-opacity group-hover:opacity-100" />
                </span>
                <div className="absolute inset-0 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-1000 group-hover:translate-x-[100%]" />
              </Button>
            </DialogTrigger>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="max-w-xs border-border/50 bg-background/95 p-3 shadow-xl backdrop-blur-sm"
          >
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                تصدير بيانات Excel
              </div>

              <div className="space-y-1 text-xs text-muted-foreground">
                {selectedCompany && (
                  <div className="flex items-center gap-1.5">
                    <Building2 className="h-3 w-3" />
                    <span>الشركة: {selectedCompany.Name}</span>
                  </div>
                )}

                <div className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" />
                  <span>
                    الفترة: {formatDate(startDate)} - {formatDate(endDate)}
                  </span>
                </div>

                <div className="border-t border-border/50 pt-1">
                  <span className="font-semibold text-primary">
                    {items.length} عنصر جاهز للتصدير
                  </span>
                </div>

                <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                  <AlertCircle className="h-3 w-3" />
                  <span>انقر لتحديد الأعمدة المطلوبة</span>
                </div>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <DialogContent
        className={cn(
          'max-h-[85vh] max-w-2xl p-0',
          'bg-gradient-to-br from-background via-background to-muted/20',
          'dark:from-slate-900 dark:via-slate-900 dark:to-slate-800/50',
          'border-border/50 shadow-2xl dark:border-slate-700/50',
        )}
      >
        <DialogHeader className="border-b border-border/30 bg-gradient-to-r from-emerald-50/50 to-green-50/50 p-6 pb-4 dark:border-slate-700/50 dark:from-emerald-950/30 dark:to-green-950/30">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                <div className="rounded-lg bg-gradient-to-br from-emerald-600 to-green-600 p-2 text-white shadow-lg">
                  <TableProperties className="h-5 w-5" />
                </div>
                تخصيص أعمدة التصدير
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                اختر الأعمدة التي تريد تضمينها في ملف Excel
              </DialogDescription>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge className="bg-gradient-to-r from-blue-600 to-blue-500 text-white">
                {selectedColumns.size} / {COLUMN_CONFIG.length} محدد
              </Badge>
              {items.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  {items.length} سجل
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 p-6">
          {/* Select All Toggle */}
          <Card
            className={cn(
              'border-2 p-4 transition-all duration-200',
              selectAll
                ? 'border-emerald-500 bg-emerald-50/50 dark:border-emerald-600/50 dark:bg-emerald-950/30'
                : 'border-border/50 bg-muted/30 dark:border-slate-700/50 dark:bg-slate-800/30',
            )}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'rounded-lg p-2 transition-colors',
                    selectAll
                      ? 'bg-gradient-to-br from-emerald-600 to-green-600 text-white shadow-md'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  {selectAll ? (
                    <ToggleRight className="h-4 w-4" />
                  ) : (
                    <ToggleLeft className="h-4 w-4" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-semibold">تحديد الكل</p>
                  <p className="text-xs text-muted-foreground">
                    {selectAll
                      ? 'جميع الأعمدة محددة'
                      : 'انقر لتحديد جميع الأعمدة'}
                  </p>
                </div>
              </div>
              <Switch
                checked={selectAll}
                onCheckedChange={handleSelectAll}
                className="data-[state=checked]:bg-emerald-600 dark:border-slate-600 dark:data-[state=checked]:bg-emerald-500"
              />
            </div>
          </Card>

          <Separator className="my-4" />

          {/* Grouped Columns */}
          <ScrollArea className="h-[calc(85vh-24rem)] pr-4">
            <div className="space-y-4">
              {groupedColumns.map(([category, group]) => {
                const CategoryIcon = group.icon;
                const categoryColumns = group.columns;
                const allSelected = categoryColumns.every((col) =>
                  selectedColumns.has(col.key),
                );
                const someSelected = categoryColumns.some((col) =>
                  selectedColumns.has(col.key),
                );

                return (
                  <Card
                    key={category}
                    className="overflow-hidden border-border/50 dark:border-slate-700/50 dark:bg-slate-800/20"
                  >
                    <div
                      className={cn(
                        'cursor-pointer bg-gradient-to-r p-3 transition-all duration-200',
                        allSelected
                          ? 'border-b-2 border-primary/30 from-primary/10 to-primary/5 dark:border-emerald-600/50 dark:from-emerald-600/20 dark:to-emerald-700/10'
                          : 'border-b border-border/30 from-muted/50 to-muted/30 dark:border-slate-700/50 dark:from-slate-800/50 dark:to-slate-700/30',
                        'hover:from-primary/15 hover:to-primary/10 dark:hover:from-emerald-600/25 dark:hover:to-emerald-700/15',
                      )}
                      onClick={() => handleCategoryToggle(categoryColumns)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CategoryIcon
                            className={cn(
                              'h-4 w-4',
                              allSelected
                                ? 'text-primary'
                                : 'text-muted-foreground',
                            )}
                          />
                          <span className="text-sm font-semibold">
                            {group.label}
                          </span>
                          <Badge
                            variant="outline"
                            className={cn(
                              'h-5 px-1.5 text-[10px]',
                              allSelected &&
                                'border-primary/30 bg-primary/10 text-primary',
                            )}
                          >
                            {
                              categoryColumns.filter((col) =>
                                selectedColumns.has(col.key),
                              ).length
                            }
                            /{categoryColumns.length}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          {someSelected && !allSelected && (
                            <div className="h-2 w-2 animate-pulse rounded-full bg-amber-500" />
                          )}
                          <Checkbox
                            checked={allSelected}
                            className="data-[state=checked]:border-primary data-[state=checked]:bg-primary dark:border-slate-600 dark:data-[state=checked]:border-emerald-600 dark:data-[state=checked]:bg-emerald-600"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 p-3">
                      {categoryColumns.map((column) => {
                        const Icon = column.icon;
                        const isSelected = selectedColumns.has(column.key);

                        return (
                          <div
                            key={column.key}
                            onClick={() => handleToggleColumn(column.key)}
                            className={cn(
                              'flex cursor-pointer items-center justify-between rounded-lg p-3',
                              'group transition-all duration-200',
                              isSelected
                                ? 'border border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5 dark:border-emerald-600/40 dark:from-emerald-600/20 dark:to-emerald-700/10'
                                : 'border border-transparent bg-muted/20 hover:bg-muted/40 dark:border-slate-700/30 dark:bg-slate-800/40 dark:hover:bg-slate-700/40',
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className={cn(
                                  'rounded-md p-1.5 transition-all duration-200',
                                  column.bgColor,
                                  isSelected && 'scale-110 shadow-sm',
                                )}
                              >
                                <Icon
                                  className={cn('h-3.5 w-3.5', column.color)}
                                />
                              </div>
                              <div>
                                <p
                                  className={cn(
                                    'text-sm font-medium',
                                    isSelected
                                      ? 'text-foreground'
                                      : 'text-muted-foreground',
                                  )}
                                >
                                  {column.label}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {column.exportLabel}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isSelected && (
                                <CheckCircle2 className="h-4 w-4 text-primary opacity-0 transition-opacity group-hover:opacity-100" />
                              )}
                              <Checkbox
                                checked={isSelected}
                                className="data-[state=checked]:border-primary data-[state=checked]:bg-primary dark:border-slate-600 dark:data-[state=checked]:border-emerald-600 dark:data-[state=checked]:bg-emerald-600"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                );
              })}
            </div>
          </ScrollArea>
        </div>

        <DialogFooter className="border-t border-border/30 bg-gradient-to-r from-muted/30 to-muted/20 p-6 pt-4 dark:border-slate-700/50 dark:from-slate-800/50 dark:to-slate-700/30">
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileDown className="h-4 w-4" />
              <span>
                {selectedColumns.size === 0
                  ? 'لم يتم تحديد أي أعمدة'
                  : `سيتم تصدير ${selectedColumns.size} عمود`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className={cn(
                  'border-border/50',
                  'bg-background/80 hover:bg-muted/50',
                  'dark:bg-slate-800/50 dark:hover:bg-slate-700/50',
                  'dark:border-slate-700 dark:text-slate-200',
                  'transition-all duration-200',
                )}
              >
                <X className="ml-2 h-4 w-4" />
                إلغاء
              </Button>
              <Button
                onClick={handleExport}
                disabled={selectedColumns.size === 0}
                className={cn(
                  'bg-gradient-to-r from-emerald-600 to-green-600',
                  'hover:from-emerald-700 hover:to-green-700',
                  'dark:from-emerald-600 dark:to-green-600',
                  'dark:hover:from-emerald-700 dark:hover:to-green-700',
                  'text-white shadow-lg transition-all duration-200 hover:shadow-xl',
                  'border border-white/20 dark:border-emerald-500/30',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  'disabled:from-gray-400 disabled:to-gray-500',
                  'dark:disabled:from-gray-600 dark:disabled:to-gray-700',
                )}
              >
                <Download className="ml-2 h-4 w-4" />
                تصدير الآن
                {selectedColumns.size > 0 && (
                  <Badge
                    variant="outline"
                    className="mr-2 h-5 border-white/30 bg-white/20 px-1 text-[10px] font-bold text-white"
                  >
                    {selectedColumns.size}
                  </Badge>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExcelBtn;
